// Throwaway preview used only when main.tsx detects ?demo=bubbles.
// Will be removed once HistoryScreen (Phase 1.3, hh-y7i) consumes the bubbles
// in their real context. Not exported from the barrel.

import type { JSX } from 'preact';
import { T } from '../../tokens';
import { UserBubble } from './UserBubble';
import { AIText } from './AIText';
import { AISummary } from './AISummary';
import { AIClarify } from './AIClarify';
import type { Message } from './Message';

const SAMPLE: readonly Message[] = [
  { id: 'u1', kind: 'user', text: 'Поменял масло в Камри, 142500 км.', time: '14:02' },
  {
    id: 'a1',
    kind: 'ai_text',
    text: 'Записал: замена масла, пробег 142 500 км. Следующая через 10 000 км или год.',
    time: '14:02',
  },
  { id: 'u2', kind: 'user', text: 'А когда последний раз менял тормозную?', time: '14:03' },
  {
    id: 'a2',
    kind: 'ai_text',
    text: 'Тормозную жидкость меняли 8 марта 2024 года. Пора заменить.',
    time: '14:03',
  },
  { id: 'u3', kind: 'user', text: 'Спасибо.' },

  // Phase 1.5 — AISummary, single action (create)
  { id: 'u4', kind: 'user', text: 'Купил палатку, кладу в кладовку на даче.', time: '15:10' },
  {
    id: 'a3',
    kind: 'ai_summary',
    text: 'Готово — записал.',
    actions: [{ kind: 'create', name: 'палатка', to: 'Кладовка · Дача' }],
    undoable: true,
    agentActionId: 'act-001',
    time: '15:10',
  },

  // Phase 1.5 — AISummary, multi-action (case-04 multistep)
  {
    id: 'u5',
    kind: 'user',
    text: 'Купил удочки, палатку и спальник, всё в кладовку. Старую палатку выкидываю.',
    time: '15:12',
  },
  {
    id: 'a4',
    kind: 'ai_summary',
    text: 'Готово — обновил три записи.',
    actions: [
      { kind: 'create', name: 'удочки', to: 'Кладовка · Дача' },
      { kind: 'move', name: 'спальник', from: 'Гараж', to: 'Кладовка · Дача' },
      { kind: 'delete', name: 'старая палатка' },
    ],
    undoable: true,
    agentActionId: 'act-002',
    time: '15:12',
  },

  // Phase 1.5 — AISummary, undo window closed (no buttons useful)
  {
    id: 'a5',
    kind: 'ai_summary',
    text: 'Запись недельной давности — окно отмены закрыто.',
    actions: [{ kind: 'create', name: 'фонарик', to: 'Кладовка · Дача' }],
    undoable: false,
    time: 'вчера',
  },

  // Phase 1.6 — AIClarify, unresolved (case-28 inline переспрос)
  { id: 'u6', kind: 'user', text: 'Положи удочки в кладовку.', time: '15:20' },
  {
    id: 'a6',
    kind: 'ai_clarify',
    text: 'Две кладовки — в какую положить?',
    options: [
      { id: 'opt-dacha', label: 'Кладовка · Дача', hint: '3 полки, рыболовное' },
      { id: 'opt-flat', label: 'Кладовка · Квартира', hint: 'инструменты, бытовое' },
    ],
    time: '15:20',
  },

  // Phase 1.6 — AIClarify, resolved (chosen option is highlighted)
  {
    id: 'a7',
    kind: 'ai_clarify',
    text: 'Где именно лежит ключ? Две связки.',
    options: [
      { id: 'k-car', label: 'Ключи · Машина' },
      { id: 'k-home', label: 'Ключи · Дом' },
    ],
    resolvedOptionId: 'k-home',
    time: '15:22',
  },
];

function renderBubble(msg: Message): JSX.Element {
  switch (msg.kind) {
    case 'user':
      // Pass time only when defined — exactOptionalPropertyTypes is strict.
      return msg.time !== undefined ? (
        <UserBubble text={msg.text} time={msg.time} />
      ) : (
        <UserBubble text={msg.text} />
      );
    case 'ai_text':
      return msg.time !== undefined ? (
        <AIText text={msg.text} time={msg.time} />
      ) : (
        <AIText text={msg.text} />
      );
    case 'ai_summary':
      // Demo wires console callbacks so reviewers can see button behaviour.
      return (
        <AISummary
          msg={msg}
          onUndo={(id) => console.log('[demo] undo', id)}
          onOpenTree={() => console.log('[demo] open tree')}
          onAddMore={() => console.log('[demo] add more')}
        />
      );
    case 'ai_clarify':
      return (
        <AIClarify msg={msg} onPick={(o) => console.log('[demo] pick', o.id, o.label)} />
      );
    default: {
      // Exhaustiveness check — when later phases widen Message, the compiler
      // forces us to update this switch.
      const _exhaustive: never = msg;
      return <span>{String(_exhaustive)}</span>;
    }
  }
}

export function BubbleDemo(): JSX.Element {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.bg,
        fontFamily: T.font,
        padding: '24px 0',
      }}
    >
      <div
        style={{
          maxWidth: 480,
          margin: '0 auto',
          background: T.bgAlt,
          borderRadius: 24,
          padding: '16px 0',
          boxShadow: T.shadow2,
        }}
      >
        <div
          style={{
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: T.ink3,
            padding: '4px 20px 12px',
          }}
        >
          BubbleDemo · ?demo=bubbles
        </div>
        {SAMPLE.map((m) => (
          <div key={m.id}>{renderBubble(m)}</div>
        ))}
      </div>
    </div>
  );
}
